import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';

const PROGRAMMES = [
  {
    id: '01',
    level: 'Nursery to Grade 2',
    ageRange: 'Ages 3–8',
    icon: 'fa-star',
    title: 'Foundational Programme',
    tagline: 'Confidence through play, exploration and early discovery.',
    description:
      'A nurturing, curiosity-led environment where young learners build strong foundations in language, literacy, numeracy and social understanding through storytelling, purposeful play and hands-on experiences.',
    keyFocus: ['Curiosity', 'Security', 'Strong Foundations'],
    img: '/images/our-program/foundation-1.webp',
    link: '/foundational-programme',
  },
  {
    id: '02',
    level: 'Grades 3–5',
    ageRange: 'Ages 8–11',
    icon: 'fa-lightbulb',
    title: 'Preparatory Programme',
    tagline: 'Structured learning that turns curiosity into capability.',
    description:
      'Children reason, communicate and collaborate with growing independence. Practical tasks, creative projects and real-life applications help them connect concepts and solve problems with clear purpose.',
    keyFocus: ['Understanding', 'Application', 'Independence'],
    img: '/images/our-program/preparatory-1.webp',
    link: '/preparatory-programme',
  },
  {
    id: '03',
    level: 'Grades 6–8',
    ageRange: 'Ages 11–14',
    icon: 'fa-atom',
    title: 'Middle School Programme',
    tagline: 'Deeper enquiry, analysis and competency-led challenge.',
    description:
      'Learners engage with complex concepts, advanced enrichment and competency-led tasks that challenge them to question, investigate and apply knowledge beyond familiar situations, supported by personalised mentoring.',
    keyFocus: ['Depth', 'Enquiry', 'Capability'],
    img: '/images/our-program/middle-1.webp',
    link: '/middle-school-programme',
  },
  {
    id: '04',
    level: 'Grades 9–12',
    ageRange: 'Ages 14–18',
    icon: 'fa-graduation-cap',
    title: 'Senior School Programme',
    tagline: 'Academic rigour balanced with readiness for the future.',
    description:
      'Board examination preparation meets competency-based assessment, competitive readiness, interdisciplinary thinking and career exploration, with dedicated mentoring for strong self-awareness and clear direction.',
    keyFocus: ['Mastery', 'Direction', 'Readiness'],
    img: '/images/our-program/senior-1.webp',
    link: '/senior-school-programme',
  },
];

const DEFAULT_SECTIONS = {
  hero: {
    key: 'hero',
    type: 'hero',
    image: '/images/page-banners/our-programme.webp',
    image_alt: 'Our Programmes — St. Andrews World School, Indirapuram',
  },
  intro: {
    key: 'intro',
    type: 'intro',
    kicker: 'Our Philosophy',
    title: 'A seamless progression from curiosity to confidence.',
    description:
      'At St. Andrews World School, Indirapuram, learning is designed as a seamless progression—from early curiosity to independent thought, and from guided discovery to confident decision-making.\n\nOur holistic educational approach combines academic depth with competency-based learning, experiential education, physical fitness and emotional well-being, so every child is ready for the world beyond the classroom.',
    image: '/images/our-program/foundation-2.webp',
    image_alt: 'St. Andrews World School, Indirapuram campus',
  },
  programmes_header: {
    key: 'programmes_header',
    type: 'header',
    kicker: 'Academic Programmes',
    title: 'A purposeful journey at every stage.',
    description: 'From foundational confidence to senior school readiness, each programme builds naturally on the one before it.',
  },
  ...PROGRAMMES.reduce((acc, item) => {
    acc[`programme_${item.title.split(' ')[0].toLowerCase()}`] = {
      key: `programme_${item.title.split(' ')[0].toLowerCase()}`,
      type: 'programme',
      level: item.level,
      age_range: item.ageRange,
      icon: item.icon,
      title: item.title,
      tagline: item.tagline,
      description: item.description,
      key_focus: item.keyFocus.join('\n'),
      image: item.img,
      link: item.link,
    };
    return acc;
  }, {}),
};

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const allDefaults = () => Object.values(DEFAULT_SECTIONS).map((s) => ({ id: 0, ...s }));

const OurProgrammesPage = () => {
  const title = 'Our Programmes';
  const [sections, setSections] = useState(allDefaults());

  useEffect(() => {
    let mounted = true;
    const fetchSections = async () => {
      try {
        const res = await API.get('/programmes/sections');
        if (!mounted) return;
        if (res.data?.data?.length) {
          setSections(res.data.data);
        }
      } catch (err) {
        /* fall back to defaults */
      }
    };
    fetchSections();
    return () => { mounted = false; };
  }, []);

  const byKey = sections.reduce((acc, s) => {
    acc[s.section_key] = s;
    return acc;
  }, {});
  const hero = byKey.hero || DEFAULT_SECTIONS.hero;
  const intro = byKey.intro || DEFAULT_SECTIONS.intro;
  const header = byKey.programmes_header || DEFAULT_SECTIONS.programmes_header;
  const programmes = sections.filter((s) => s.section_type === 'programme').length
    ? sections.filter((s) => s.section_type === 'programme')
    : PROGRAMMES.map((p) => DEFAULT_SECTIONS[Object.keys(DEFAULT_SECTIONS).find((k) => k.startsWith('programme_') && DEFAULT_SECTIONS[k].title === p.title)]);

  const introParas = (intro.description || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <>
      <SEO
        slug="our-programmes"
        title={title}
        description="Explore the comprehensive academic and co-curricular programmes at St. Andrews World School, Indirapuram, designed to nurture potential beyond the classroom."
        keywords="Our Programmes, SAWS Indirapuram, ICSE school Indirapuram, holistic education"
      />

      <section className="page-hero">
        <img
          src={resolveImage(hero.image)}
          alt={hero.image_alt || title}
          className="page-hero-img"
          loading="eager"
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner"></div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 md:gap-16 items-center">
            <Reveal className="order-2 lg:order-1">
              <img
                src={resolveImage(intro.image)}
                alt={intro.image_alt || intro.title}
                className="w-full aspect-[4/3] object-cover rounded-2xl shadow-xl"
                loading="lazy"
              />
            </Reveal>
            <div className="order-1 lg:order-2">
              <Reveal>
                <div className="kicker">{intro.kicker}</div>
                <h2 className="h-section text-3xl md:text-4xl font-bold mt-2 text-left">
                  {intro.title}
                </h2>
                <div className="divider-line mt-4" />
              </Reveal>
              <div className="mt-6 space-y-5 text-body text-[17px] leading-relaxed">
                {introParas.map((para, index) => (
                  <Reveal key={index} delayMs={80 + index * 80}>
                    <p>{para}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="academic-programmes" className="section bg-stone-50">
        <div className="container">
          <Reveal>
            <div className="section-title text-center">
              <div className="kicker">{header.kicker}</div>
              <h2 className="h-section text-3xl md:text-4xl font-bold">
                {header.title}
              </h2>
              {header.description && <p className="text-center">{header.description}</p>}
              <div className="divider-line" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-12">
            {programmes.map((item, index) => {
              const focusList = (item.key_focus || '').split(/\n/).map((f) => f.trim()).filter(Boolean);
              return (
                <Reveal key={item.id || item.section_key || index} delayMs={index * 80}>
                  <article className="group bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <Link to={item.link} className="block">
                      <div className="relative overflow-hidden">
                        <img
                          src={resolveImage(item.image)}
                          alt={item.title}
                          className="w-full h-[350px] md:h-[350px] object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-5 flex items-center gap-2">
                          <span className="bg-gold text-[#0d1b3e] font-bold text-sm px-3 py-1 rounded-full">
                            {item.age_range}
                          </span>
                          <span className="bg-white/90 text-primary text-sm font-semibold px-3 py-1 rounded-full">
                            {item.level}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 md:p-8">
                        <div className="flex items-center gap-3">
                          <div className="grid h-11 w-11 shrink-0 place-items-center bg-primary/5 text-primary rounded-lg group-hover:bg-primary group-hover:text-white transition-all duration-300">
                            <i className={`fas ${item.icon} text-lg`} />
                          </div>
                          <div>
                            <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-gold">
                              Programme {String(index + 1).padStart(2, '0')}
                            </span>
                            <h3 className="h-section text-xl md:text-2xl font-bold text-gray-900 leading-tight group-hover:text-primary transition-colors">
                              {item.title}
                            </h3>
                          </div>
                        </div>
                        <p className="mt-4 text-primary/80 font-semibold italic leading-relaxed">
                          {item.tagline}
                        </p>
                        <p className="mt-3 text-body leading-relaxed">{item.description}</p>
                        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                          {focusList.map((focus) => (
                            <span
                              key={focus}
                              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
                            >
                              <i className="fas fa-check-circle text-gold" />
                              {focus}
                            </span>
                          ))}
                        </div>
                        <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary border-b-2 border-gold/40 pb-1 group-hover:text-gold transition-colors">
                          Explore the Programme
                          <i className="fas fa-arrow-right text-[12px] transition-transform duration-300 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Link>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default OurProgrammesPage;